import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Upload, Copy, Check, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Turn an internal media URL (returned by the API) into a fully-qualified one. */
export function absMediaUrl(path) {
    if (!path) return "";
    if (path.startsWith("http")) return path;
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
}

export default function MediaLibrary({ selectable = false, onSelect, selectedIds = [] }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const fileInput = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/media`, { withCredentials: true });
            setItems(data);
        } catch {
            toast.error("Chargement impossible.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onFiles = async (files) => {
        if (!files || files.length === 0) return;
        setUploading(true);
        let success = 0;
        for (const f of files) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                await axios.post(`${API}/admin/media`, fd, {
                    withCredentials: true,
                    headers: { "Content-Type": "multipart/form-data" },
                });
                success++;
            } catch (e) {
                const msg = e?.response?.data?.detail || `Échec pour ${f.name}`;
                toast.error(typeof msg === "string" ? msg : `Échec pour ${f.name}`);
            }
        }
        setUploading(false);
        if (success) toast.success(`${success} image(s) importée(s).`);
        await load();
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement cette image ?")) return;
        try {
            await axios.delete(`${API}/admin/media/${id}`, { withCredentials: true });
            toast.success("Image supprimée.");
            setItems((prev) => prev.filter((it) => it.id !== id));
        } catch (e) {
            const msg = e?.response?.data?.detail || "Suppression impossible.";
            toast.error(typeof msg === "string" ? msg : "Suppression impossible.");
        }
    };

    const copyUrl = async (id) => {
        const url = absMediaUrl(`/api/media/${id}`);
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(id);
            toast.success("URL copiée.");
            setTimeout(() => setCopiedId(null), 1500);
        } catch {
            toast.error("Copie impossible.");
        }
    };

    return (
        <section data-testid="media-library" className="space-y-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="overline text-[#64748B]">Bibliothèque</div>
                    <div className="font-serif text-2xl md:text-3xl">Médias</div>
                    <div className="text-xs text-[#64748B] mt-1">
                        {items.length} image{items.length > 1 ? "s" : ""} · JPEG / PNG / WebP · 10 Mo max ·
                        redimensionnées automatiquement à 1920 px
                    </div>
                </div>

                <div>
                    <input
                        ref={fileInput}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        multiple
                        className="hidden"
                        onChange={(e) => onFiles(Array.from(e.target.files || []))}
                        data-testid="media-upload-input"
                    />
                    <button
                        onClick={() => fileInput.current?.click()}
                        disabled={uploading}
                        data-testid="media-upload-btn"
                        className="text-sm px-5 py-3 bg-[#1E3A5F] text-white hover:bg-[#152a45] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} strokeWidth={1.5} />}
                        Importer des images
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center gap-2 text-[#64748B]"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-slate-300 p-16 text-center text-[#64748B]">
                    <ImageIcon size={28} strokeWidth={1.2} className="mx-auto mb-3" />
                    Aucune image pour l'instant. Cliquez sur « Importer des images » pour commencer.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {items.map((m) => {
                        const isSelected = selectedIds.includes(m.id);
                        return (
                            <div
                                key={m.id}
                                data-testid={`media-${m.id}`}
                                className={`group relative bg-white border ${
                                    isSelected ? "border-[#1E3A5F] ring-2 ring-[#1E3A5F]" : "border-slate-200"
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => selectable && onSelect?.(m)}
                                    disabled={!selectable}
                                    className={`block w-full aspect-square overflow-hidden bg-[#F4F2EE] ${
                                        selectable ? "cursor-pointer" : "cursor-default"
                                    }`}
                                >
                                    <img
                                        src={absMediaUrl(`/api/media/${m.id}`)}
                                        alt={m.original_filename}
                                        loading="lazy"
                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </button>
                                <div className="p-3 flex items-center justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="text-xs font-medium truncate" title={m.original_filename}>
                                            {m.original_filename}
                                        </div>
                                        <div className="text-[10px] text-[#64748B]">
                                            {m.width}×{m.height} · {(m.size / 1024).toFixed(0)} Ko
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button
                                            type="button"
                                            onClick={() => copyUrl(m.id)}
                                            className="p-2 text-[#64748B] hover:text-[#1E3A5F]"
                                            aria-label="Copier l'URL"
                                            data-testid={`media-copy-${m.id}`}
                                        >
                                            {copiedId === m.id ? <Check size={14} /> : <Copy size={14} strokeWidth={1.5} />}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => remove(m.id)}
                                            className="p-2 text-[#64748B] hover:text-[#DC2626]"
                                            aria-label="Supprimer"
                                            data-testid={`media-delete-${m.id}`}
                                        >
                                            <Trash2 size={14} strokeWidth={1.5} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
